class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, open = true) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      if (open) {
        this.open();
      }
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    });

    // Growth Suite can apply its personalized GSC_* discount a moment after
    // the add-to-cart response is rendered. Re-read the real Shopify cart and
    // refresh the native drawer once the discount is present.
    this.syncGrowthSuiteDiscount();
  }

  async syncGrowthSuiteDiscount() {
    const syncId = (this._growthSyncId || 0) + 1;
    this._growthSyncId = syncId;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const waits = [250, 500, 750, 1000, 1500, 2000];

    for (const wait of waits) {
      await new Promise((resolve) => setTimeout(resolve, wait));
      if (this._growthSyncId !== syncId) return;

      try {
        const cartResponse = await fetch(`${root}cart.js`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
        });

        if (!cartResponse.ok) continue;
        const cart = await cartResponse.json();

        const hasGrowthCode =
          Array.isArray(cart.discount_codes) &&
          cart.discount_codes.some(
            (discount) =>
              discount &&
              discount.applicable !== false &&
              typeof discount.code === 'string' &&
              /^GSC_/i.test(discount.code)
          );

        const hasGrowthAllocation =
          Array.isArray(cart.items) &&
          cart.items.some(
            (item) =>
              Array.isArray(item.line_level_discount_allocations) &&
              item.line_level_discount_allocations.some((allocation) => {
                const title = allocation?.discount_application?.title;
                return typeof title === 'string' && /^GSC_/i.test(title);
              })
          );

        if (!hasGrowthCode && !hasGrowthAllocation) continue;

        const sectionIds = this.getSectionsToRender().map((section) => section.id);
        const refreshResponse = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            updates: {},
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });

        if (!refreshResponse.ok) return;
        const refreshedState = await refreshResponse.json();

        this._growthSyncId += 1;

        this.querySelector('.drawer__inner')?.classList.remove('is-empty');
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);

          const sectionHtml = refreshedState.sections?.[section.id];
          if (!sectionElement || !sectionHtml) return;

          sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
        });

        this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
        return;
      } catch (error) {
        // Silent retry: cart must keep working even if a refresh request fails.
      }
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-quick',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-icon-quick',
        section: 'cart-icon-quick',
        selector: '.shopify-section',
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
